import { type Column, createColumnHelper } from '@tanstack/react-table'
import { StyleSheet, css } from 'aphrodite'
import { AnimatePresence, motion } from 'framer-motion'
import type React from 'react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import ActionMenu from '../components/ActionMenu'
import ConfirmationDialog from '../components/ConfirmationDialog'
import DateRangeFilter from '../components/DateRangeFilter'
import NotesPreview from '../components/NotesPreview'
import OpenMap from '../components/OpenMaps'
import PhoneContact from '../components/PhoneContact'
import SearchBar from '../components/SearchBar'
import SkeletonLoader from '../components/SkeletonLoader'
import Table from '../components/dashboard/Table'
import ContractorOnboardingModal from '../components/onboarding/ContractorOnboardingModal'
import AcceptProjectModal from '../components/project/AcceptProjectModal'
import AddProjectModal, {
  PROJECT_DRAFT_KEY,
} from '../components/project/AddProjectModal'
import TemplateSelector from '../components/project/TemplateSelector'
import ViewProjectModal from '../components/project/ViewProjectModal'
import AddWorkOrderModal, {
  WORKORDER_DRAFT_KEY,
} from '../components/workorder/AddWorkOrderModal'
import DispatchTemplateSelectorModal from '../components/workorder/DispatchTemplateSelectorModal'
import WorkOrderSubRow from '../components/workorder/WorkOrderSubRow'
import type {
  ProjectTemplateRecipientType,
  SenderStakeholderType,
} from '../config/templateConfig'
import { useProjectContext } from '../contexts/ProjectContext'
import { useUser } from '../contexts/UserContext'
import { useWorkOrderContext } from '../contexts/WorkOrderContext'
import { type ProjectFilters, projectService } from '../services/projectService'
import { userService } from '../services/userService'
import type { Project } from '../types/project'
import { ProjectStatus } from '../types/project'
import type { Contact, User } from '../types/user'
import { StakeholderType, UserType } from '../types/user'
import {
  getBrokerNames,
  getContractorNames,
} from '../utils/relationshipHelpers'
import { getStatusRowStyle } from '../utils/statusColors'

interface ProjectFormData {
  broker: string
  subhauler: string
  inviteEmail?: string
  inviteRole?: string
}

const formatDate = (dateString: string | null) => {
  if (!dateString) return '-'
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US') // DD/MM/YYYY format
}

// Helper function to format date and time together from datetime string
const formatDateTimeFromString = (
  dateTimeString: string | null | undefined,
): string => {
  if (!dateTimeString) return '-'
  try {
    const date = new Date(dateTimeString)

    // Format date part
    const dateFormatted = formatDate(dateTimeString)

    // Format time part
    let hours = date.getHours()
    const minutes = date.getMinutes()
    const period = hours >= 12 ? 'PM' : 'AM'

    // Convert to 12-hour format
    if (hours > 12) {
      hours = hours - 12
    } else if (hours === 0) {
      hours = 12
    }

    const timeFormatted = `${hours}:${minutes
      .toString()
      .padStart(2, '0')} ${period}`

    return `${dateFormatted} ${timeFormatted}`
  } catch (error) {
    console.error('Error formatting date/time:', error)
    return '-'
  }
}

const ProjectsDashboard: React.FC = () => {
  const { projects, refreshProjects, deleteProject } = useProjectContext()
  const { workOrders } = useWorkOrderContext()
  const location = useLocation()
  const navigate = useNavigate()
  const [isMobile] = useState(window.innerWidth < 1024)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [selectedProject, setSelectedProject] = useState<Project | null>(null)
  const [selectedWorkOrderProject, setSelectedWorkOrderProject] =
    useState<Project | null>(null)
  const [selectedAcceptProject, setSelectedAcceptProject] =
    useState<Project | null>(null)
  const [selectedViewProject, setSelectedViewProject] =
    useState<Project | null>(null)
  const { user: currentUser } = useUser()
  const [contacts, setContacts] = useState<Contact[]>([])
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [totalCount, setTotalCount] = useState<number | undefined>(undefined)
  const [totalPages, setTotalPages] = useState<number | undefined>(undefined)
  const [contractorOnboardingOpen, setContractorOnboardingOpen] =
    useState(false)
  const [dateFrom, setDateFrom] = useState<Date | null>(null)
  const [dateTo, setDateTo] = useState<Date | null>(null)
  const [filteredProjectsData, setFilteredProjectsData] = useState<Project[]>(
    [],
  )
  const [isFiltering, setIsFiltering] = useState(false)
  const [searchValue, setSearchValue] = useState<string>('')
  const [activeSearch, setActiveSearch] = useState<string>('')
  const [projectCreators, setProjectCreators] = useState<
    Map<number, { name: string; id: number; company?: string }>
  >(new Map())
  const [selectedTemplateType, setSelectedTemplateType] =
    useState<ProjectTemplateRecipientType | null>(null)
  const [selectedDispatchTemplateType, setSelectedDispatchTemplateType] =
    useState<ProjectTemplateRecipientType | null>(null)
  const [pendingDispatchProject, setPendingDispatchProject] =
    useState<Project | null>(null)
  const [projectToDelete, setProjectToDelete] = useState<Project | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const creatorCacheRef = useRef<
    Map<number, { name: string; id: number; company?: string }>
  >(new Map())
  const lastFetchParamsRef = useRef<string>('')
  const hasFetchedContactsRef = useRef(false)
  const lastCreatorFetchIdsRef = useRef<string>('')

  const currentTab = location.pathname.includes('accepted')
    ? 'accepted'
    : location.pathname.includes('declined')
      ? 'declined'
      : location.pathname.includes('drafts')
        ? 'drafts'
        : 'pending'

  // Use filtered data if filtering is active, otherwise use context projects
  const projectsToUse = isFiltering ? filteredProjectsData : projects
  const safeProjects = Array.isArray(projectsToUse) ? projectsToUse : []

  const filteredProjects = safeProjects.filter((project) =>
    currentTab === 'accepted'
      ? project.status === 'ACCEPTED'
      : currentTab === 'declined'
        ? project.status === 'DECLINED'
        : currentTab === 'drafts'
          ? project.status === 'DRAFT'
          : project.status === 'PENDING',
  )

  const projectsToDisplay =
    filteredProjects.length > 0 ? filteredProjects : safeProjects

  const sortedProjects = [...filteredProjects].sort((a, b) => {
    const dateA = new Date(a.date)
    const dateB = new Date(b.date)
    return dateB.getTime() - dateA.getTime()
  })

  const fetchProjectCreators = useCallback(async (projectList: Project[]) => {
    try {
      const creatorMap = new Map<
        number,
        { name: string; id: number; company?: string }
      >()

      // Get unique creator IDs
      const creatorIds = Array.from(
        new Set(
          projectList
            .map((p) => {
              if (
                p.created_by &&
                typeof p.created_by === 'object' &&
                'id' in p.created_by
              ) {
                return (p.created_by as User).id
              }
              return null
            })
            .filter((id): id is number => id !== null),
        ),
      )

      // If no unique creators, return early
      if (creatorIds.length === 0) {
        setProjectCreators(creatorMap)
        return
      }

      // Check cache first and only fetch missing creators
      const uncachedIds: number[] = []
      for (const id of creatorIds) {
        if (creatorCacheRef.current.has(id)) {
          const cached = creatorCacheRef.current.get(id)
          if (cached) {
            creatorMap.set(id, cached)
          }
        } else {
          uncachedIds.push(id)
        }
      }

      // Only fetch uncached creators
      if (uncachedIds.length > 0) {
        const creatorPromises = uncachedIds.map(async (creatorId) => {
          try {
            const [creatorData, companyData] = await Promise.all([
              userService.getUserById(creatorId),
              userService.getUserCompanyPublic(creatorId).catch(() => null),
            ])

            if (creatorData) {
              const name =
                `${creatorData.first_name || ''} ${
                  creatorData.last_name || ''
                }`.trim() || '-'
              const company = companyData?.name || undefined
              return { id: creatorId, name, company }
            }
            return null
          } catch (error) {
            console.error(`Failed to fetch creator ${creatorId}:`, error)
            return null
          }
        })

        const results = await Promise.all(creatorPromises)

        // Update cache and map with new results
        for (const result of results) {
          if (result) {
            creatorMap.set(result.id, result)
            creatorCacheRef.current.set(result.id, result)
          }
        }
      }

      setProjectCreators(creatorMap)
    } catch (error) {
      console.error('Failed to fetch project creators:', error)
    }
  }, [])

  const fetchProjects = useCallback(async () => {
    try {
      setLoading(true)
      const filters: ProjectFilters & { page?: number; page_size?: number } = {}

      if (dateFrom) filters.dateFrom = dateFrom
      if (dateTo) filters.dateTo = dateTo
      if (activeSearch.trim()) filters.search = activeSearch.trim()
      // Send status filter for server pagination
      if (currentTab === 'accepted') {
        filters.status = ProjectStatus.ACCEPTED
      } else if (currentTab === 'declined') {
        filters.status = ProjectStatus.DECLINED
      } else if (currentTab === 'drafts') {
        filters.status = ProjectStatus.DRAFT
      } else {
        // Pending tab - only PENDING status (drafts have their own tab now)
        filters.status = ProjectStatus.PENDING
      }
      filters.page = page
      filters.page_size = pageSize

      const res = await projectService.getProjects(filters)
      setFilteredProjectsData(Array.isArray(res.items) ? res.items : [])
      setTotalCount(res.count)
      setTotalPages(res.pages)
      setIsFiltering(true)
    } catch (error) {
      console.error('Failed to fetch projects:', error)
      setIsFiltering(false)
      setFilteredProjectsData([])
    } finally {
      setLoading(false)
    }
  }, [dateFrom, dateTo, activeSearch, page, pageSize, currentTab])

  // Fetch contacts on mount
  // Ref guard prevents StrictMode double-fetch
  useEffect(() => {
    if (hasFetchedContactsRef.current) return
    hasFetchedContactsRef.current = true

    const fetchContacts = async () => {
      try {
        const contactsData = await userService.getContacts()
        setContacts(contactsData)
      } catch (error) {
        console.error('Failed to fetch contacts:', error)
      }
    }

    fetchContacts()
  }, [])

  // Separate effect to fetch creator details when projects change
  // Ref guard prevents duplicate fetches with same project IDs
  useEffect(() => {
    if (
      Array.isArray(filteredProjectsData) &&
      filteredProjectsData.length > 0
    ) {
      const projectIds = filteredProjectsData
        .map((p) => p.id)
        .sort()
        .join(',')
      if (lastCreatorFetchIdsRef.current !== projectIds) {
        lastCreatorFetchIdsRef.current = projectIds
        fetchProjectCreators(filteredProjectsData)
      }
    }
  }, [filteredProjectsData, fetchProjectCreators])

  // Fetch projects (paginated) whenever filters or pagination change
  // Ref guard prevents duplicate fetches with same params (e.g., from StrictMode)
  useEffect(() => {
    const paramsKey = JSON.stringify({
      dateFrom: dateFrom?.toISOString(),
      dateTo: dateTo?.toISOString(),
      activeSearch,
      page,
      pageSize,
      currentTab,
    })
    if (lastFetchParamsRef.current !== paramsKey) {
      lastFetchParamsRef.current = paramsKey
      fetchProjects()
    }
  }, [
    fetchProjects,
    dateFrom,
    dateTo,
    activeSearch,
    page,
    pageSize,
    currentTab,
  ])

  useEffect(() => {
    if (contractorOnboardingOpen) {
      document.body.style.overflow = 'hidden'
      document.body.style.position = 'fixed'
      document.body.style.width = '100%'
      document.body.style.top = `-${window.scrollY}px`
    } else {
      const scrollY = document.body.style.top
      document.body.style.overflow = ''
      document.body.style.position = ''
      document.body.style.width = ''
      document.body.style.top = ''
      window.scrollTo(0, Number.parseInt(scrollY || '0', 10) * -1)
    }

    return () => {
      document.body.style.overflow = ''
      document.body.style.position = ''
      document.body.style.width = ''
      document.body.style.top = ''
    }
  }, [contractorOnboardingOpen])

  // Auto-open contractor onboarding modal if user hasn't completed onboarding
  useEffect(() => {
    if (currentUser && !currentUser.has_completed_contractor_onboarding) {
      setContractorOnboardingOpen(true)
    }
  }, [currentUser])

  // Check if user is a site supervisor
  const isSiteSupervisor = currentUser?.user_type === UserType.SITE_SUPERVISOR

  // Check if current user is a Broker (for column header naming)
  const isBroker =
    currentUser?.current_profile?.stakeholder_type === StakeholderType.BROKER

  // Check if user should see the "New Project Terms" button
  const canCreateProjects =
    currentUser?.user_type !== UserType.DISPATCHER &&
    currentUser?.user_type !== UserType.SITE_SUPERVISOR

  const columnHelper = createColumnHelper<Project>()

  const getColumns = useMemo(() => {
    const baseColumns: Column<Project>[] = []

    const actionColumn = columnHelper.display({
      id: 'newWorkOrder',
      header: () => <span>ACTIONS</span>,
      cell: ({ row }) => {
        const isPending = row.original.status === 'PENDING'
        const isDraft = row.original.status === 'DRAFT'
        const isDeclined = row.original.status === 'DECLINED'

        // Handle draft projects - show "Continue Draft" to edit and "Delete Draft" to remove
        if (isDraft) {
          const actions = [
            {
              label: 'Continue Draft',
              icon: 'fa-solid fa-pen',
              onClick: () => {
                setSelectedProject(row.original)
                setSelectedTemplateType(
                  row.original
                    .template_recipient_type as ProjectTemplateRecipientType | null,
                )
              },
              className: css(styles.workOrderButton),
            },
            {
              label: 'Delete Draft',
              icon: 'fa-solid fa-trash',
              onClick: () => setProjectToDelete(row.original),
              className: css(styles.deleteButton),
            },
          ]
          return <ActionMenu actions={actions} />
        }

        // Handle declined projects - only show View Declined Project Term
        if (isDeclined) {
          const actions = [
            {
              label: 'View Declined Project Term',
              icon: 'fa-solid fa-eye',
              onClick: () => handleViewProject(row.original),
              className: css(styles.viewButton),
            },
          ]
          return <ActionMenu actions={actions} />
        }

        // Site supervisors see View Project Terms for pending, New Dispatch for accepted (but no New Tag)
        if (isSiteSupervisor) {
          const actions = isPending
            ? [
                {
                  label: 'View Project Terms – Pending',
                  icon: 'fa-solid fa-eye',
                  onClick: () => handleViewProject(row.original),
                  className: css(styles.viewButton),
                },
              ]
            : [
                {
                  label: 'New Dispatch',
                  icon: 'fa-solid fa-plus',
                  onClick: () => setPendingDispatchProject(row.original),
                  className: css(styles.workOrderButton),
                },
              ]

          return <ActionMenu actions={actions} />
        }

        const actions = [
          {
            label: isPending ? 'View Project Terms – Pending' : 'New Dispatch',
            icon: isPending ? 'fa-solid fa-eye' : 'fa-solid fa-plus',
            onClick: () =>
              isPending
                ? setSelectedAcceptProject(row.original)
                : setPendingDispatchProject(row.original),
            className: css(styles.workOrderButton),
          },
        ]

        if (!isPending) {
          // Ensure View is second: existing New Dispatch (already first), then View
          // Note: New Tag option removed from Accepted Project Terms - tags should only be created from Tags section
          actions.push({
            label: 'View Project',
            icon: 'fa-solid fa-eye',
            onClick: () => handleViewProject(row.original),
            className: css(styles.viewButton),
          })
        }

        return <ActionMenu actions={actions} />
      },
      size: 150,
    })

    const remainingColumnsDesktop = [
      columnHelper.accessor('label', {
        cell: (info) => info.getValue(),
        header: () => <span>PROJECT ID</span>,
      }),
      columnHelper.display({
        id: 'brokers',
        cell: ({ row }) => {
          const project = row.original

          // Get brokers from the project
          if (project.brokers && project.brokers.length > 0) {
            return (
              <div
                style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}
              >
                {project.brokers.map((user, index) => {
                  const displayName =
                    `${user.first_name || ''} ${user.last_name || ''}`.trim() ||
                    user.email ||
                    'Unknown'
                  return (
                    <div
                      key={user.id || index}
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '2px',
                      }}
                    >
                      <PhoneContact
                        displayName={displayName}
                        phoneNumber={user.phone_number}
                      />
                      {user.email && (
                        <a
                          href={`mailto:${user.email}`}
                          style={{
                            color: '#6B7280',
                            fontSize: '11px',
                            textDecoration: 'none',
                          }}
                        >
                          {user.email}
                        </a>
                      )}
                    </div>
                  )
                })}
              </div>
            )
          }

          // Legacy fallback for broker_contacts
          const brokerContactsLegacy = Array.isArray(project.broker_contacts)
            ? (project.broker_contacts
                .map((id) => contacts.find((contact) => contact.id === id))
                .filter(Boolean) as Contact[])
            : []
          return brokerContactsLegacy.length > 0
            ? brokerContactsLegacy
                .map(
                  (broker) =>
                    broker.contact_object?.name ||
                    broker.contact_object?.email ||
                    '',
                )
                .join(', ')
            : '-'
        },
        header: () => <span>{isBroker ? 'BROKER' : 'BROKERS'}</span>,
      }),
      columnHelper.display({
        id: 'third_party_brokers',
        cell: ({ row }) => {
          const project = row.original

          // Get third-party brokers from the project
          if (
            project.third_party_brokers &&
            project.third_party_brokers.length > 0
          ) {
            return (
              <div
                style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}
              >
                {project.third_party_brokers.map((user, index) => {
                  const displayName =
                    `${user.first_name || ''} ${user.last_name || ''}`.trim() ||
                    user.email ||
                    'Unknown'
                  return (
                    <div
                      key={user.id || index}
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '2px',
                      }}
                    >
                      <PhoneContact
                        displayName={displayName}
                        phoneNumber={user.phone_number}
                      />
                      {user.email && (
                        <a
                          href={`mailto:${user.email}`}
                          style={{
                            color: '#6B7280',
                            fontSize: '11px',
                            textDecoration: 'none',
                          }}
                        >
                          {user.email}
                        </a>
                      )}
                    </div>
                  )
                })}
              </div>
            )
          }

          return '-'
        },
        header: () => <span>THIRD-PARTY BROKERS</span>,
      }),
      columnHelper.accessor('trucks_number', {
        cell: (info) => info.getValue() || '-',
        header: () => <span>NUMBER OF TRUCKS</span>,
      }),
      columnHelper.display({
        id: 'contractors',
        cell: ({ row }) => {
          const project = row.original

          // Use contractors from snapshot data
          if (project.contractors && project.contractors.length > 0) {
            return (
              <div
                style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}
              >
                {project.contractors.map((user, index) => {
                  const displayName =
                    `${user.first_name || ''} ${user.last_name || ''}`.trim() ||
                    user.email ||
                    'Unknown'
                  return (
                    <div
                      key={user.id || index}
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '2px',
                      }}
                    >
                      <PhoneContact
                        displayName={displayName}
                        phoneNumber={user.phone_number}
                      />
                      {user.email && (
                        <a
                          href={`mailto:${user.email}`}
                          style={{
                            color: '#6B7280',
                            fontSize: '11px',
                            textDecoration: 'none',
                          }}
                        >
                          {user.email}
                        </a>
                      )}
                    </div>
                  )
                })}
              </div>
            )
          }

          return '-'
        },
        header: () => <span>CONTRACTORS</span>,
      }),
      columnHelper.display({
        id: 'subhaulers',
        cell: ({ row }) => {
          const project = row.original

          // Collect all sub haulers from multiple sources
          const allSubhaulerMap = new Map<number, User>()

          // 1. Get sub haulers directly assigned to the project (from template)
          if (project.sub_haulers && project.sub_haulers.length > 0) {
            for (const user of project.sub_haulers) {
              allSubhaulerMap.set(user.id, user)
            }
          }

          // 2. Get sub haulers from related work orders
          const projectWorkOrders = (workOrders || []).filter((wo) => {
            const projectId =
              typeof wo.project === 'object' ? wo.project?.id : wo.project
            return projectId === project.id
          })
          const workOrderSubhaulers = projectWorkOrders.flatMap(
            (wo) => wo.subhaulers || [],
          )
          for (const user of workOrderSubhaulers) {
            if (!allSubhaulerMap.has(user.id)) {
              allSubhaulerMap.set(user.id, user)
            }
          }

          const uniqueSubhaulers = [...allSubhaulerMap.values()]

          if (uniqueSubhaulers.length > 0) {
            return (
              <div
                style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}
              >
                {uniqueSubhaulers.map((user, index) => {
                  const displayName =
                    `${user.first_name || ''} ${user.last_name || ''}`.trim() ||
                    user.email ||
                    'Unknown'
                  return (
                    <div
                      key={user.id || index}
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '2px',
                      }}
                    >
                      <PhoneContact
                        displayName={displayName}
                        phoneNumber={user.phone_number}
                      />
                      {user.email && (
                        <a
                          href={`mailto:${user.email}`}
                          style={{
                            color: '#6B7280',
                            fontSize: '11px',
                            textDecoration: 'none',
                          }}
                        >
                          {user.email}
                        </a>
                      )}
                    </div>
                  )
                })}
              </div>
            )
          }
          return '-'
        },
        header: () => <span>SUB HAULERS</span>,
      }),
      columnHelper.display({
        id: 'third_party_contractors',
        cell: ({ row }) => {
          const project = row.original

          // Get third party contractors directly assigned to the project (from template)
          if (
            project.third_party_contractors &&
            project.third_party_contractors.length > 0
          ) {
            return (
              <div
                style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}
              >
                {project.third_party_contractors.map(
                  (user: User, index: number) => {
                    const displayName =
                      `${user.first_name || ''} ${user.last_name || ''}`.trim() ||
                      user.email ||
                      'Unknown'
                    return (
                      <div
                        key={user.id || index}
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '2px',
                        }}
                      >
                        <PhoneContact
                          displayName={displayName}
                          phoneNumber={user.phone_number}
                        />
                        {user.email && (
                          <a
                            href={`mailto:${user.email}`}
                            style={{
                              color: '#6B7280',
                              fontSize: '11px',
                              textDecoration: 'none',
                            }}
                          >
                            {user.email}
                          </a>
                        )}
                      </div>
                    )
                  },
                )}
              </div>
            )
          }
          return '-'
        },
        header: () => <span>THIRD PARTY CONTRACTORS</span>,
      }),
      columnHelper.display({
        id: 'site_supervisors',
        cell: ({ row }) => {
          const project = row.original

          // Display site supervisor from the text form fields
          const firstName = project.site_supervisor_first_name || ''
          const lastName = project.site_supervisor_last_name || ''
          const fullName = `${firstName} ${lastName}`.trim()
          const phone = project.site_supervisor_phone || ''
          const email = project.site_supervisor_email || ''

          if (fullName || email) {
            const displayName = fullName || email || 'Unknown'
            return (
              <div
                style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}
              >
                <PhoneContact displayName={displayName} phoneNumber={phone} />
                {email && (
                  <a
                    href={`mailto:${email}`}
                    style={{
                      color: '#6B7280',
                      fontSize: '11px',
                      textDecoration: 'none',
                    }}
                  >
                    {email}
                  </a>
                )}
              </div>
            )
          }

          // Fallback to M2M relationship if text fields are empty
          if (project.site_supervisors && project.site_supervisors.length > 0) {
            return (
              <div
                style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}
              >
                {project.site_supervisors.map((user: User, index: number) => {
                  const userDisplayName =
                    `${user.first_name} ${user.last_name}`.trim() ||
                    user.email ||
                    'Unknown'
                  return (
                    <div
                      key={user.id || index}
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '2px',
                      }}
                    >
                      <PhoneContact
                        displayName={userDisplayName}
                        phoneNumber={user.phone_number}
                      />
                      {user.email && (
                        <a
                          href={`mailto:${user.email}`}
                          style={{
                            color: '#6B7280',
                            fontSize: '11px',
                            textDecoration: 'none',
                          }}
                        >
                          {user.email}
                        </a>
                      )}
                    </div>
                  )
                })}
              </div>
            )
          }

          return '-'
        },
        header: () => <span>SITE SUPERVISORS</span>,
      }),
      columnHelper.accessor('job_site', {
        cell: (info) => info.getValue() || '-',
        header: () => <span>JOB SITE</span>,
      }),
      columnHelper.accessor('job_id', {
        cell: (info) => info.getValue(),
        header: () => <span>JOB ID</span>,
      }),
      columnHelper.accessor('starting_date', {
        cell: (info) => formatDateTimeFromString(info.getValue()),
        header: () => <span>START DATE</span>,
      }),
      columnHelper.accessor('completion_date', {
        cell: (info) => formatDate(info.getValue()),
        header: () => <span>COMPLETION DATE</span>,
      }),
      columnHelper.accessor('pickup_address', {
        cell: (info) =>
          info.getValue() ? <OpenMap address={info.getValue()} /> : '-',
        header: () => <span>PICK UP LOCATION</span>,
      }),
      columnHelper.accessor('dropoff_address', {
        cell: (info) =>
          info.getValue() ? <OpenMap address={info.getValue()} /> : '-',
        header: () => <span>DROP OFF LOCATION</span>,
      }),
      columnHelper.accessor('material', {
        cell: (info) => info.getValue(),
        header: () => <span>MATERIAL</span>,
      }),
      // Only show rate type and pay rate columns for non-site supervisors
      ...(currentUser?.user_type !== 'SITE_SUPERVISOR'
        ? [
            columnHelper.accessor('rate_type', {
              cell: (info) => info.getValue(),
              header: () => <span>RATE TYPE</span>,
            }),
            columnHelper.accessor('pay_rate', {
              cell: (info) => info.getValue(),
              header: () => <span>PAY RATE</span>,
            }),
          ]
        : []),
      columnHelper.accessor('invoice_interval', {
        cell: (info) =>
          info.getValue().charAt(0).toUpperCase() + info.getValue().slice(1),
        header: () => <span>INVOICES</span>,
      }),
      columnHelper.accessor('status_interval', {
        cell: (info) =>
          info.getValue().charAt(0).toUpperCase() + info.getValue().slice(1),
        header: () => <span>STATUS REPORTS</span>,
      }),
      columnHelper.accessor('notes', {
        cell: (info) => <NotesPreview note={info.getValue()} />,
        header: () => <span>NOTES</span>,
      }),
      columnHelper.display({
        id: 'attachments',
        cell: ({ row }) => {
          const count = row.original.attachments?.length ?? 0
          if (count === 0) return '-'
          return (
            <span className={css(styles.attachmentCount)}>
              <i className="fa-solid fa-paperclip" />
              {count}
            </span>
          )
        },
        header: () => <span>FILES</span>,
        size: 80,
      }),
    ]

    const remainingColumnsMobile = [
      columnHelper.accessor('label', {
        cell: (info) => info.getValue(),
        header: () => <span>ID</span>,
      }),
      columnHelper.accessor('job_site', {
        cell: (info) => info.getValue() || '-',
        header: () => <span>JOB SITE</span>,
      }),
      columnHelper.accessor('starting_date', {
        cell: (info) => formatDateTimeFromString(info.getValue()),
        header: () => <span>START</span>,
      }),
      columnHelper.accessor('material', {
        cell: (info) => info.getValue(),
        header: () => <span>MATERIAL</span>,
      }),
    ]

    const rem = remainingColumnsDesktop
    return currentTab === 'accepted'
      ? [...baseColumns, actionColumn, ...rem]
      : [...baseColumns, actionColumn, ...rem]
  }, [
    currentTab,
    currentUser,
    contacts,
    isSiteSupervisor,
    isBroker,
    columnHelper,
    workOrders,
  ])

  const columns = getColumns

  const renderSubRow = useCallback(
    (project: Project) => {
      return (
        <WorkOrderSubRow
          workOrders={workOrders}
          projectId={project.id?.toString() || ''}
          columnsCount={columns.length}
        />
      )
    },
    [workOrders, columns.length],
  )

  const handleSubmit = (data: ProjectFormData) => {
    console.log(data)
    setIsModalOpen(false)
  }

  const handleViewProject = (project: Project) => {
    setSelectedViewProject(project)
  }

  const handleSearch = () => {
    setActiveSearch(searchValue)
  }

  const handleSearchClear = () => {
    setSearchValue('')
    setActiveSearch('')
  }

  const handleDeleteDraft = async () => {
    if (!projectToDelete) return
    setIsDeleting(true)
    try {
      await deleteProject(projectToDelete.id)
      setProjectToDelete(null)
      fetchProjects()
    } catch (error) {
      console.error('Failed to delete draft:', error)
    } finally {
      setIsDeleting(false)
    }
  }

  return (
    <div className={css(styles.projectsDashboard)}>
      <ContractorOnboardingModal
        open={contractorOnboardingOpen}
        onClose={() => setContractorOnboardingOpen(false)}
      />
      <div className={css(styles.mainContent)}>
        <div className={css(styles.header)}>
          <div className={css(styles.topRow)}>
            <div className={css(styles.titleSection)}>
              <h2 className={css(styles.title)}>
                Project Terms{' '}
                {currentTab === 'pending'
                  ? '- Pending'
                  : currentTab === 'declined'
                    ? '- Declined'
                    : currentTab === 'drafts'
                      ? '- Draft'
                      : '- Accepted'}
              </h2>
            </div>
            {canCreateProjects && (
              <TemplateSelector
                entityType="project"
                onSelect={(templateType) => {
                  // Clear any existing draft when starting fresh
                  localStorage.removeItem(PROJECT_DRAFT_KEY)
                  setSelectedTemplateType(
                    templateType as ProjectTemplateRecipientType,
                  )
                  setIsModalOpen(true)
                }}
                buttonLabel={isMobile ? 'Project Terms' : 'New Project Terms'}
                userStakeholderType={
                  currentUser?.current_profile
                    ?.stakeholder_type as SenderStakeholderType
                }
              />
            )}
          </div>

          <div className={css(styles.filtersRow)}>
            <DateRangeFilter
              dateFrom={dateFrom}
              dateTo={dateTo}
              onDateFromChange={setDateFrom}
              onDateToChange={setDateTo}
              onClear={() => {
                setDateFrom(null)
                setDateTo(null)
              }}
              showYearMonthDropdowns={true}
            />
            <SearchBar
              searchValue={searchValue}
              onSearchChange={setSearchValue}
              onSearch={handleSearch}
              onClear={handleSearchClear}
              placeholder="Search job sites, materials, labels, and more..."
            />
          </div>
        </div>

        <div className={css(styles.tableContainer)}>
          <AnimatePresence mode="wait">
            <motion.div
              key={currentTab}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.2 }}
              className={css(styles.tableWrapper)}
            >
              {loading ? (
                <SkeletonLoader
                  rows={8}
                  columns={6}
                  showHeader={true}
                  className={css(styles.skeletonLoader)}
                />
              ) : (
                <Table
                  data={sortedProjects}
                  columns={columns}
                  renderSubRow={renderSubRow}
                  getRowStyle={(project) => getStatusRowStyle(project.status)}
                  page={page}
                  pages={totalPages}
                  count={totalCount}
                  pageSize={pageSize}
                  onPageChange={setPage}
                  onPageSizeChange={(size) => {
                    setPageSize(size)
                    setPage(1)
                  }}
                />
              )}
            </motion.div>
          </AnimatePresence>
        </div>

        {(isModalOpen || selectedProject) && (
          <AddProjectModal
            onClose={() => {
              setIsModalOpen(false)
              setSelectedProject(null)
              setSelectedTemplateType(null)
            }}
            project={selectedProject}
            templateRecipientType={selectedTemplateType}
            onSuccess={() => {
              // Ensure dashboard reflects the newly created/updated project terms immediately
              setPage(1)
              fetchProjects()
            }}
            readOnly={
              selectedProject
                ? String(currentUser?.id) !==
                  String((selectedProject.created_by as User).id)
                : false
            }
          />
        )}

        {selectedWorkOrderProject && selectedDispatchTemplateType && (
          <AddWorkOrderModal
            project={selectedWorkOrderProject}
            templateRecipientType={selectedDispatchTemplateType}
            onClose={() => {
              setSelectedWorkOrderProject(null)
              setSelectedDispatchTemplateType(null)
            }}
          />
        )}

        {pendingDispatchProject && !selectedDispatchTemplateType && (
          <DispatchTemplateSelectorModal
            onSelect={(templateType) => {
              // Clear any existing draft when starting fresh
              localStorage.removeItem(WORKORDER_DRAFT_KEY)
              setSelectedDispatchTemplateType(templateType)
              setSelectedWorkOrderProject(pendingDispatchProject)
              setPendingDispatchProject(null)
            }}
            onClose={() => setPendingDispatchProject(null)}
          />
        )}
        {selectedAcceptProject && (
          <AcceptProjectModal
            project={selectedAcceptProject}
            onClose={() => setSelectedAcceptProject(null)}
            onSuccess={() => {
              setPage(1)
              fetchProjects()
            }}
          />
        )}
        {selectedViewProject && (
          <ViewProjectModal
            project={selectedViewProject}
            onClose={() => setSelectedViewProject(null)}
          />
        )}
        {projectToDelete && (
          <ConfirmationDialog
            title="Delete Draft"
            message="Are you certain you want to permanently delete this draft? It will no longer be available or accessible to you."
            confirmLabel="Yes, delete"
            cancelLabel="Back, I made an error"
            onConfirm={handleDeleteDraft}
            onCancel={() => setProjectToDelete(null)}
            isDestructive={true}
            reverseButtons={true}
            isLoading={isDeleting}
          />
        )}
      </div>
    </div>
  )
}

const styles = StyleSheet.create({
  projectsDashboard: {
    display: 'flex',
    padding: '16px',
    backgroundColor: '#F9FAFB',
    minHeight: '100vh',
    width: 'calc(100vw - 280px)',
    boxSizing: 'border-box',
    overflowX: 'hidden',
    '@media (max-width: 1024px)': {
      padding: '12px',
      width: '100vw',
    },
  },
  mainContent: {
    flex: 1,
    width: '100%',
    boxSizing: 'border-box',
  },
  header: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    padding: '20px',
    '@media (max-width: 1024px)': {
      flexDirection: 'column',
      alignItems: 'stretch',
      gap: '16px',
      padding: '12px',
    },
  },
  topRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  filtersRow: {
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'flex-start',
    flexWrap: 'wrap',
    gap: '16px',
    '@media (max-width: 1024px)': {
      flexDirection: 'column',
      alignItems: 'stretch',
      gap: '12px',
    },
  },
  title: {
    margin: 0,
    fontSize: '24px',
    fontWeight: 600,
    '@media (max-width: 1024px)': {
      fontSize: '14px',
    },
  },
  newProjectButton: {
    backgroundColor: '#1C64F2',
    color: 'white',
    padding: '10px 20px',
    borderRadius: '6px',
    border: 'none',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '14px',
    fontWeight: 500,
    minHeight: '44px',
    ':hover': {
      backgroundColor: '#1955CC',
    },
    '@media (max-width: 1024px)': {
      padding: '4px 8px',
      fontSize: '10px',
      borderRadius: '6px',
      minHeight: '28px',
    },
  },
  tableContainer: {
    borderRadius: '8px',
    padding: '16px',
    width: '100%',
    boxSizing: 'border-box',
    marginTop: '-20px',
    overflowX: 'hidden',
  },
  tableControls: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '16px',
  },
  filterButton: {
    padding: '8px 16px',
    borderRadius: '6px',
    border: '1px solid #E5E7EB',
    backgroundColor: 'white',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '14px',
  },
  tableActions: {
    display: 'flex',
    gap: '8px',
  },
  actionButton: {
    padding: '8px',
    borderRadius: '6px',
    border: '1px solid #E5E7EB',
    backgroundColor: 'white',
    cursor: 'pointer',
  },
  tableWrapper: {
    width: '100%',
    position: 'relative',
  },
  editButton: {
    padding: '8px',
    borderRadius: '6px',
    border: '1px solid #E5E7EB',
    backgroundColor: 'white',
    cursor: 'pointer',
    color: '#6B7280',
    ':hover': {
      backgroundColor: '#F3F4F6',
      color: '#1C64F2',
    },
  },
  viewButton: {
    backgroundColor: '#1C64F2',
    color: 'white',
    padding: '8px 12px',
    borderRadius: '6px',
    border: 'none',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '12px',
    marginTop: '4px',
    fontWeight: 500,
    ':hover': {
      backgroundColor: '#1955CC',
    },
    '@media (max-width: 1024px)': {
      fontSize: '10px',
      padding: '6px 8px',
    },
  },
  workOrderButton: {
    backgroundColor: '#1C64F2',
    color: 'white',
    padding: '8px 12px',
    borderRadius: '6px',
    border: 'none',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '12px',
    marginTop: '4px',
    fontWeight: 500,
    ':hover': {
      backgroundColor: '#1955CC',
    },
    '@media (max-width: 1024px)': {
      fontSize: '10px',
      padding: '6px 8px',
    },
  },
  deleteButton: {
    backgroundColor: '#DC2626',
    color: 'white',
    padding: '8px 12px',
    borderRadius: '6px',
    border: 'none',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '12px',
    marginTop: '4px',
    fontWeight: 500,
    ':hover': {
      backgroundColor: '#B91C1C',
    },
    '@media (max-width: 1024px)': {
      fontSize: '10px',
      padding: '6px 8px',
    },
  },
  titleSection: {
    display: 'flex',
    alignItems: 'center',
  },
  actionButtons: {
    display: 'flex',
    gap: '8px',
    flexDirection: 'column',
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '50vh',
    color: '#6B7280',
  },
  loadingText: {
    marginTop: '16px',
    fontSize: '16px',
  },
  skeletonHeader: {
    display: 'grid',
    gridTemplateColumns: '2fr 1fr 1fr 0.8fr 0.8fr 1.2fr 1.2fr 0.8fr',
    backgroundColor: '#F9FAFB',
    borderBottom: '1px solid #E5E7EB',
    gap: '1px',
    minWidth: '1200px',
  },
  skeletonRow: {
    display: 'grid',
    gridTemplateColumns: '2fr 1fr 1fr 0.8fr 0.8fr 1.2fr 1.2fr 0.8fr',
    borderBottom: '1px solid #E5E7EB',
    gap: '1px',
    minWidth: '1200px',
    ':hover': {
      backgroundColor: '#F9FAFB',
    },
  },
  skeletonCell: {
    padding: '12px 16px',
    backgroundColor: '#F3F4F6',
    borderRadius: '4px',
    margin: '4px',
    animation: 'pulse 1.5s ease-in-out infinite',
    height: '20px',
  },
  '@keyframes pulse': {
    '0%, 100%': {
      opacity: 1,
    },
    '50%': {
      opacity: 0.5,
    },
  },
  skeletonLoader: {
    margin: '0',
    border: 'none',
    borderRadius: '8px',
  },
  attachmentCount: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    color: '#6B7280',
    fontSize: '13px',
  },
})

export default ProjectsDashboard
